import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import AdminLayout from '../components/AdminLayout';
import { Button, Card, Input, Select, Modal, EmptyState, Skeleton, StatusPill, useToast } from '../../design-system';
import {
  formatServiceName,
  FREQUENCY_OPTIONS,
  shortDueRule,
  describeDueRule,
  previewDueDates,
  formatDate,
  offsetToMonthIndex,
  monthIndexToOffset,
  MONTHS,
  shortPrice,
  describePrice,
  suggestBillingBasis,
  BILLING_BASIS_OPTIONS,
  FEE_KIND_OPTIONS,
} from '../../lib/serviceCatalog';
import '../styles/AdminServices.css';

const GROUP_ICONS = {
  'Income Tax': '💰',
  'ROC / Company Law / LODR': '🏛️',
  'Accounting': '📒',
  'Audit': '📋',
  'GST': '🧾',
  'IEC / FEMA': '🌐',
  'Payroll': '💵',
};
const iconForGroup = (name) => GROUP_ICONS[name] || '🗂️';

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Opening the editor on an undated service must NOT pre-load a plausible-looking
// rule — that is exactly how "Monthly, 20th, +1mo" got saved onto Company
// Formation and Annual Return File. Start with the frequency unset so the admin
// has to make a real choice.
const emptyRuleForm = { frequency: '', dueDay: '', monthOffset: 1 };
const emptyAddForm = { groupName: '', name: '' };
// For the same reason as emptyRuleForm: an unpriced service opens with a blank
// amount. A suggested fee that nobody actually chose is worse than none, since
// every firm sees it as the platform's recommendation.
const emptyPriceForm = { amount: '', billingBasis: 'per_filing', kind: 'professional_fees', notes: '' };

function AdminServices() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState(new Set());
  const toast = useToast();

  // "Used by" column: bulk per-service counts (loaded once), then the org
  // names for one service are fetched lazily when its badge is clicked.
  const [usageSummary, setUsageSummary] = useState({ totalOrgs: 0, usage: {} });
  const [usageModal, setUsageModal] = useState(null); // { service, loading, organizations, error }

  // Due-rule editor modal
  const [editing, setEditing] = useState(null); // the service being edited
  const [ruleForm, setRuleForm] = useState(emptyRuleForm);
  const [ruleError, setRuleError] = useState('');
  const [savingRule, setSavingRule] = useState(false);

  // Suggested-price editor modal. What the platform proposes as a starting
  // rate; every org overrides it with its own on their Services page, and that
  // override always wins. Nothing here is binding on anyone.
  const [pricing, setPricing] = useState(null); // the service being priced
  const [priceForm, setPriceForm] = useState(emptyPriceForm);
  const [priceError, setPriceError] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  // Add-service modal
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [addError, setAddError] = useState('');
  const [addSaving, setAddSaving] = useState(false);

  // Disabled Services — system-wide (global catalog + every org's own custom
  // services), fetched separately from the active catalog above. This is the
  // one place any of it stays visible at all; everywhere else (this org's
  // Services page, task creation, client enrollment) already filters
  // disabled rows out via isActive.
  const [disabledList, setDisabledList] = useState([]);
  const [disabledLoading, setDisabledLoading] = useState(true);
  const [disabledOpen, setDisabledOpen] = useState(false);
  const [togglingActiveId, setTogglingActiveId] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchUsageSummary();
    fetchDisabledList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/services');
      setServices(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const fetchDisabledList = async () => {
    try {
      setDisabledLoading(true);
      const res = await apiClient.get('/api/services/disabled');
      setDisabledList(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load disabled services');
    } finally {
      setDisabledLoading(false);
    }
  };

  // One bulk call for every service's usage count, so the column renders
  // without a request per row. Silent on failure — the column just falls
  // back to "—" per row rather than blocking the whole page.
  const fetchUsageSummary = async () => {
    try {
      const res = await apiClient.get('/api/services/usage-summary');
      setUsageSummary(res.data);
    } catch (err) {
      // no-op — usage badges degrade to "—"
    }
  };

  const openUsageModal = async (service) => {
    setUsageModal({ service, loading: true, organizations: [], error: '' });
    try {
      const res = await apiClient.get(`/api/services/${service._id}/usage`);
      setUsageModal({ service, loading: false, organizations: res.data.organizations, error: '' });
    } catch (err) {
      setUsageModal({
        service,
        loading: false,
        organizations: [],
        error: err.response?.data?.error || 'Failed to load organizations',
      });
    }
  };

  const goToOrganization = (orgId) => {
    setUsageModal(null);
    navigate(`/admin/organizations/${orgId}`);
  };

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? services.filter(
          (s) => s.name.toLowerCase().includes(term) || s.groupName.toLowerCase().includes(term)
        )
      : services;

    const byGroup = new Map();
    filtered.forEach((s) => {
      const key = s.groupName.trim().toLowerCase();
      if (!byGroup.has(key)) byGroup.set(key, { key, displayName: s.groupName, items: [] });
      byGroup.get(key).items.push(s);
    });
    return Array.from(byGroup.values()).sort((a, b) => b.items.length - a.items.length);
  }, [services, search]);

  const totals = useMemo(() => ({
    all: services.length,
    dated: services.filter((s) => s.defaultDueRule).length,
    priced: services.filter((s) => s.defaultPrice).length,
    groups: new Set(services.map((s) => s.groupName.trim().toLowerCase())).size,
    disabled: disabledList.length,
  }), [services, disabledList]);

  const isSearching = search.trim() !== '';

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Based on the full unfiltered catalog (not the search-narrowed `groups`),
  // so the button's label/action stays meaningful even mid-search.
  const allGroupKeys = useMemo(
    () => Array.from(new Set(services.map((s) => s.groupName.trim().toLowerCase()))),
    [services]
  );
  const allExpanded = allGroupKeys.length > 0 && allGroupKeys.every((k) => openGroups.has(k));
  const toggleAllGroups = () => setOpenGroups(allExpanded ? new Set() : new Set(allGroupKeys));

  const openRuleEditor = (service) => {
    setEditing(service);
    setRuleError('');
    setRuleForm(
      service.defaultDueRule
        ? {
            frequency: service.defaultDueRule.frequency,
            dueDay: service.defaultDueRule.dueDay,
            monthOffset: service.defaultDueRule.monthOffset ?? 1,
          }
        : emptyRuleForm
    );
  };

  const applyRuleUpdate = (serviceId, defaultDueRule) => {
    setServices((prev) => prev.map((s) => (s._id === serviceId ? { ...s, defaultDueRule } : s)));
  };

  // The concrete dates the rule currently in the form would generate this FY.
  // Carries the service's existing exceptions (e.g. TDS Q4) so the preview
  // matches what the engine will actually produce.
  const rulePreview = useMemo(() => {
    const dueDay = Number(ruleForm.dueDay);
    if (!ruleForm.frequency || !Number.isInteger(dueDay) || dueDay < 1 || dueDay > 31) return [];
    return previewDueDates({
      frequency: ruleForm.frequency,
      dueDay,
      monthOffset: Number(ruleForm.monthOffset) || 0,
      exceptions: editing?.defaultDueRule?.exceptions || [],
    });
  }, [ruleForm, editing]);

  const handleSaveRule = async () => {
    if (!ruleForm.frequency) {
      setRuleError('Pick a frequency first');
      return;
    }

    // One-off, engagement-driven work has no statutory calendar date. Saving
    // "one-time" therefore CLEARS the rule rather than inventing a fake
    // deadline — this is what stops Company Formation ending up "due the 20th
    // of every month".
    if (ruleForm.frequency === 'one_time') {
      await handleClearRule();
      return;
    }

    const dueDay = Number(ruleForm.dueDay);
    const monthOffset = Number(ruleForm.monthOffset);
    if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 31) {
      setRuleError('Due day must be between 1 and 31');
      return;
    }
    if (!Number.isInteger(monthOffset) || monthOffset < 0) {
      setRuleError('Month offset must be 0 or more');
      return;
    }

    setSavingRule(true);
    try {
      const defaultDueRule = { frequency: ruleForm.frequency, dueDay, monthOffset };
      const res = await apiClient.put(`/api/services/${editing._id}`, { defaultDueRule });
      applyRuleUpdate(editing._id, res.data.defaultDueRule);
      toast.success('Default due date saved');
      setEditing(null);
    } catch (err) {
      setRuleError(err.response?.data?.error || 'Failed to save due date');
    } finally {
      setSavingRule(false);
    }
  };

  const handleClearRule = async () => {
    setSavingRule(true);
    try {
      await apiClient.put(`/api/services/${editing._id}`, { defaultDueRule: null });
      applyRuleUpdate(editing._id, null);
      toast.success('Due date cleared');
      setEditing(null);
    } catch (err) {
      setRuleError(err.response?.data?.error || 'Failed to clear due date');
    } finally {
      setSavingRule(false);
    }
  };

  const openPriceEditor = (service) => {
    setPricing(service);
    setPriceError('');
    setPriceForm(
      service.defaultPrice
        ? {
            amount: service.defaultPrice.amount ?? '',
            billingBasis: service.defaultPrice.billingBasis || 'per_filing',
            kind: service.defaultPrice.kind || 'professional_fees',
            notes: service.defaultPrice.notes || '',
          }
        : { ...emptyPriceForm, billingBasis: suggestBillingBasis(service.defaultDueRule) }
    );
  };

  const applyPriceUpdate = (serviceId, defaultPrice) => {
    setServices((prev) => prev.map((s) => (s._id === serviceId ? { ...s, defaultPrice } : s)));
  };

  const handleSavePrice = async () => {
    const amount = Number(priceForm.amount);
    if (priceForm.amount === '' || !Number.isFinite(amount) || amount < 0) {
      setPriceError('Enter an amount of 0 or more');
      return;
    }
    setSavingPrice(true);
    try {
      const res = await apiClient.put(`/api/services/${pricing._id}`, {
        defaultPrice: {
          amount,
          billingBasis: priceForm.billingBasis,
          kind: priceForm.kind,
          notes: priceForm.notes,
        },
      });
      applyPriceUpdate(pricing._id, res.data.defaultPrice);
      toast.success('Suggested price saved');
      setPricing(null);
    } catch (err) {
      setPriceError(err.response?.data?.error || 'Failed to save price');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleClearPrice = async () => {
    setSavingPrice(true);
    try {
      await apiClient.put(`/api/services/${pricing._id}`, { defaultPrice: null });
      applyPriceUpdate(pricing._id, null);
      // Only the SUGGESTION goes. Any firm that set its own rate keeps it —
      // priceOverride lives on their ServiceSelection and is untouched by this.
      toast.success('Suggested price cleared');
      setPricing(null);
    } catch (err) {
      setPriceError(err.response?.data?.error || 'Failed to clear price');
    } finally {
      setSavingPrice(false);
    }
  };

  // Disabling a GLOBAL service (isActive: false) is what makes it vanish
  // everywhere at once — every other consumer (org Services pages, the task
  // and client-enrollment service pickers) already filters to isActive:true
  // by default, so this one flag is the single source of truth. Moves the
  // service out of the active catalog above and into Disabled Services below.
  const toggleServiceActive = async (service, isActive) => {
    if (!isActive && !window.confirm(`Disable "${formatServiceName(service.name)}"? It will disappear from every organization immediately.`)) {
      return;
    }
    setTogglingActiveId(service._id);
    try {
      await apiClient.put(`/api/services/${service._id}`, { isActive });
      toast.success(`${formatServiceName(service.name)} ${isActive ? 'enabled' : 'disabled'}`);
      await Promise.all([fetchServices(), fetchDisabledList()]);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update service');
    } finally {
      setTogglingActiveId(null);
    }
  };

  // Re-enabling a row in the Disabled Services section — routed by scope
  // since a global row and an org's own custom row live under different
  // endpoints (a super admin has no org of their own to match a custom
  // row's organizationId against, hence the dedicated /org/:orgId path).
  const enableDisabledService = async (service) => {
    setTogglingActiveId(service._id);
    try {
      if (service.scope === 'org') {
        await apiClient.put(`/api/services/org/${service.organizationId}/${service._id}/active`, { isActive: true });
      } else {
        await apiClient.put(`/api/services/${service._id}`, { isActive: true });
      }
      toast.success(`${formatServiceName(service.name)} enabled`);
      await Promise.all([fetchServices(), fetchDisabledList()]);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to enable service');
    } finally {
      setTogglingActiveId(null);
    }
  };

  const openAdd = () => {
    setAddForm(emptyAddForm);
    setAddError('');
    setAddOpen(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    if (!addForm.groupName.trim() || !addForm.name.trim()) {
      setAddError('Group and service name are required');
      return;
    }
    setAddSaving(true);
    try {
      // Must include disabled global rows too, or a new service's itemId
      // could collide with one a disabled row already occupies.
      const allKnown = [...services, ...disabledList.filter((s) => s.scope === 'global')];
      const existingGroup = allKnown.find(
        (s) => s.groupName.toLowerCase() === addForm.groupName.trim().toLowerCase()
      );
      const nextItemId = allKnown.reduce((max, s) => Math.max(max, s.itemId), 0) + 1;
      await apiClient.post('/api/services', {
        groupId: existingGroup ? existingGroup.groupId : Math.max(0, ...allKnown.map((s) => s.groupId)) + 1,
        groupName: addForm.groupName.trim(),
        itemId: nextItemId,
        name: addForm.name.trim(),
      });
      toast.success('Service added to global catalog');
      setAddOpen(false);
      fetchServices();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add service');
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <AdminLayout
      title="Services Catalog"
      subtitle="The global service catalog and each service's default statutory due date"
    >
      <div className="admin-services-page">
        {!loading && (
          <div className="admin-services-summary">
            <div className="admin-services-stat">
              <span className="admin-services-number">{totals.all}</span>
              <span className="admin-services-label">Global Services</span>
            </div>
            <div className="admin-services-stat">
              <span className="admin-services-number">{totals.dated}</span>
              <span className="admin-services-label">With Due Date</span>
            </div>
            <div className="admin-services-stat">
              <span className="admin-services-number">{totals.priced}</span>
              <span className="admin-services-label">With Price</span>
            </div>
            <div className="admin-services-stat">
              <span className="admin-services-number">{totals.groups}</span>
              <span className="admin-services-label">Categories</span>
            </div>
            <div className="admin-services-stat">
              <span className="admin-services-number">{totals.disabled}</span>
              <span className="admin-services-label">Disabled</span>
            </div>
          </div>
        )}

        <div className="admin-services-toolbar">
          <Input
            className="admin-services-search"
            placeholder="🔎 Search services…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="secondary" onClick={toggleAllGroups}>
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
          <Button variant="primary" onClick={openAdd}>+ Add Service</Button>
        </div>

        {loading ? (
          <Card><Skeleton.Text lines={6} /></Card>
        ) : groups.length === 0 ? (
          <EmptyState icon="🗂️" title="No services found" description="Try a different search term." />
        ) : (
          <div className="admin-services-groups">
            {groups.map(({ key, displayName: groupName, items }) => {
              const datedCount = items.filter((s) => s.defaultDueRule).length;
              const isOpen = isSearching || openGroups.has(key);
              return (
                <Card key={key} className="admin-service-group">
                  <button
                    type="button"
                    className="admin-service-group-header"
                    onClick={() => toggleGroup(key)}
                    aria-expanded={isOpen}
                  >
                    <span className="admin-service-group-icon">{iconForGroup(groupName)}</span>
                    <div className="admin-service-group-title">
                      <div className="admin-service-group-name">{groupName}</div>
                      <div className="admin-service-group-sub">{datedCount} of {items.length} dated</div>
                    </div>
                    <span className={`admin-service-group-chevron ${isOpen ? 'admin-service-group-chevron-open' : ''}`}>
                      <ChevronIcon />
                    </span>
                  </button>

                  {isOpen && (
                    <div className="admin-service-rows">
                      {items.map((s) => {
                        const usingCount = usageSummary.usage[s._id];
                        return (
                          <div key={s._id} className="admin-service-row">
                            <span className="admin-service-name">{formatServiceName(s.name)}</span>
                            <button
                              type="button"
                              className="admin-service-usedby"
                              onClick={() => openUsageModal(s)}
                              disabled={usingCount === undefined}
                              title="See which organizations use this service"
                            >
                              🏢 {usingCount === undefined ? '—' : `${usingCount}/${usageSummary.totalOrgs}`} orgs
                            </button>
                            {s.defaultDueRule ? (
                              <StatusPill variant="info" label={shortDueRule(s.defaultDueRule)} />
                            ) : (
                              <span className="admin-service-nodate">No due date</span>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openRuleEditor(s)}>
                              {s.defaultDueRule ? 'Edit' : 'Set due date'}
                            </Button>
                            {/* Pill and button share one grid cell — the row is
                                already five columns wide, and splitting these
                                would make it seven. */}
                            <div
                              className="admin-service-price"
                              title={s.defaultPrice ? describePrice(s.defaultPrice) : 'No suggested price'}
                            >
                              {s.defaultPrice ? (
                                <StatusPill variant="success" label={shortPrice(s.defaultPrice)} />
                              ) : (
                                <span className="admin-service-nodate">No price</span>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => openPriceEditor(s)}>
                                {s.defaultPrice ? 'Edit' : 'Suggest price'}
                              </Button>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="admin-service-disable-btn"
                              loading={togglingActiveId === s._id}
                              onClick={() => toggleServiceActive(s, false)}
                            >
                              Disable
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {!disabledLoading && disabledList.length > 0 && (
          <Card className="admin-disabled-section">
            <button
              type="button"
              className="admin-service-group-header"
              onClick={() => setDisabledOpen((v) => !v)}
              aria-expanded={disabledOpen}
            >
              <span className="admin-service-group-icon admin-disabled-icon">🚫</span>
              <div className="admin-service-group-title">
                <div className="admin-service-group-name">Disabled Services</div>
                <div className="admin-service-group-sub">
                  {disabledList.length} — global catalog and every org's own custom services, visible only to you
                </div>
              </div>
              <span className={`admin-service-group-chevron ${disabledOpen ? 'admin-service-group-chevron-open' : ''}`}>
                <ChevronIcon />
              </span>
            </button>

            {disabledOpen && (
              <div className="admin-service-rows">
                {disabledList.map((s) => (
                  <div key={s._id} className="admin-service-row admin-disabled-row">
                    <div className="admin-disabled-info">
                      <span className="admin-service-name">{formatServiceName(s.name)}</span>
                      <span className="admin-disabled-group">{s.groupName}</span>
                    </div>
                    <StatusPill
                      variant={s.scope === 'org' ? 'warning' : 'neutral'}
                      label={s.scope === 'org' ? s.ownerName : 'Platform (Global)'}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={togglingActiveId === s._id}
                      onClick={() => enableDisabledService(s)}
                    >
                      Enable
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Due-rule editor */}
      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Due date · ${formatServiceName(editing.name)}` : 'Due date'}
        footer={
          <div className="admin-rule-footer">
            {editing?.defaultDueRule && (
              <Button variant="danger" onClick={handleClearRule} disabled={savingRule}>
                Clear
              </Button>
            )}
            <div className="admin-rule-footer-right">
              <Button variant="secondary" onClick={() => setEditing(null)} disabled={savingRule}>Cancel</Button>
              <Button variant="primary" onClick={handleSaveRule} loading={savingRule}>Save</Button>
            </div>
          </div>
        }
      >
        <div className="admin-rule-form">
          {ruleError && <div className="form-error" role="alert">{ruleError}</div>}
          <Select
            label="Frequency"
            value={ruleForm.frequency}
            onChange={(e) => setRuleForm({ ...ruleForm, frequency: e.target.value })}
          >
            <option value="">— Select a frequency —</option>
            {FREQUENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.value === 'one_time' ? 'One-time (no due date)' : o.label}
              </option>
            ))}
          </Select>
          {ruleForm.frequency === 'one_time' ? (
            <p className="hint-text">
              One-off work has no statutory due date — it&apos;s driven by the engagement, not the
              calendar. Saving will clear any due-date rule on this service, and its task will be
              created when a client is enrolled.
            </p>
          ) : (
          <div className="admin-rule-grid">
            <Input
              label="Due day (of month)"
              type="number"
              min={1}
              max={31}
              value={ruleForm.dueDay}
              onChange={(e) => setRuleForm({ ...ruleForm, dueDay: e.target.value })}
              helperText="31 = last day of the month"
            />
            {/* An annual rule has ONE date a year, so asking for "months after
                period end" is needlessly cryptic — let the admin pick the month
                and convert to the offset the engine stores. */}
            {ruleForm.frequency === 'annual' ? (
              <Select
                label="Due month"
                value={String(offsetToMonthIndex(Number(ruleForm.monthOffset) || 0))}
                onChange={(e) =>
                  setRuleForm({ ...ruleForm, monthOffset: monthIndexToOffset(Number(e.target.value)) })
                }
                helperText="For a 31 Mar year-end"
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </Select>
            ) : (
            <Input
              label="Months after period end"
              type="number"
              min={0}
              value={ruleForm.monthOffset}
              onChange={(e) => setRuleForm({ ...ruleForm, monthOffset: e.target.value })}
              helperText="0 = same month, 1 = following month"
            />
            )}
          </div>
          )}

          {ruleForm.frequency !== 'one_time' && (
            <>
              <div className="admin-rule-preview">
                {describeDueRule({
                  frequency: ruleForm.frequency,
                  dueDay: Number(ruleForm.dueDay) || 1,
                  monthOffset: Number(ruleForm.monthOffset) || 0,
                })}
              </div>

              {/* The actual dates the engine will generate — the only way an
                  admin can tell "day 31, +4" really means 31 July. */}
              {rulePreview.length > 0 && (
                <div className="admin-rule-dates">
                  <div className="admin-rule-dates-title">This financial year, that means:</div>
                  {rulePreview.map((p) => (
                    <div key={p.label} className="admin-rule-dates-row">
                      <span>{p.label}</span>
                      <strong>{formatDate(p.date)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Used-by list */}
      <Modal
        isOpen={!!pricing}
        onClose={() => setPricing(null)}
        title={pricing ? `Suggested price · ${formatServiceName(pricing.name)}` : 'Suggested price'}
        footer={
          <div className="admin-rule-footer">
            {pricing?.defaultPrice && (
              <Button variant="danger" onClick={handleClearPrice} disabled={savingPrice}>
                Clear
              </Button>
            )}
            <div className="admin-rule-footer-right">
              <Button variant="secondary" onClick={() => setPricing(null)} disabled={savingPrice}>Cancel</Button>
              <Button variant="primary" onClick={handleSavePrice} loading={savingPrice}>Save</Button>
            </div>
          </div>
        }
      >
        <div className="admin-rule-form">
          {priceError && <div className="form-error" role="alert">{priceError}</div>}
          <p className="hint-text">
            A starting suggestion only. Every firm sets its own rate for this service on their
            Services page, and theirs always wins — clearing this never changes a price a firm has
            already set for itself.
          </p>
          <div className="admin-rule-grid">
            <Input
              label="Amount"
              type="number"
              min={0}
              step="0.01"
              value={priceForm.amount}
              onChange={(e) => setPriceForm({ ...priceForm, amount: e.target.value })}
            />
            <Select
              label="Charged"
              value={priceForm.billingBasis}
              onChange={(e) => setPriceForm({ ...priceForm, billingBasis: e.target.value })}
            >
              {BILLING_BASIS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </div>
          <Select
            label="Fee type"
            value={priceForm.kind}
            onChange={(e) => setPriceForm({ ...priceForm, kind: e.target.value })}
          >
            {FEE_KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
          <Input
            label="Note"
            placeholder="e.g. per return, up to 50 invoices"
            value={priceForm.notes}
            onChange={(e) => setPriceForm({ ...priceForm, notes: e.target.value })}
          />
          {priceForm.amount !== '' && Number.isFinite(Number(priceForm.amount)) && (
            <div className="admin-rule-preview">
              {describePrice({
                amount: Number(priceForm.amount),
                billingBasis: priceForm.billingBasis,
                kind: priceForm.kind,
              })}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={!!usageModal}
        onClose={() => setUsageModal(null)}
        title={usageModal ? `Used by · ${formatServiceName(usageModal.service.name)}` : 'Used by'}
        footer={<Button variant="secondary" onClick={() => setUsageModal(null)}>Close</Button>}
      >
        {usageModal?.loading ? (
          <Skeleton.Text lines={3} />
        ) : usageModal?.error ? (
          <div className="form-error" role="alert">{usageModal.error}</div>
        ) : usageModal?.organizations.length === 0 ? (
          <EmptyState icon="🏢" title="No organizations use this service" description="Every org has this switched off." />
        ) : (
          <ul className="admin-usedby-list">
            {usageModal?.organizations.map((org) => (
              <li key={org._id}>
                <button type="button" className="admin-usedby-org" onClick={() => goToOrganization(org._id)}>
                  <span>{org.name}</span>
                  <span className="admin-usedby-arrow">→</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      {/* Add service */}
      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Service"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleAdd} loading={addSaving}>Add Service</Button>
          </>
        }
      >
        <form className="admin-rule-form" onSubmit={handleAdd}>
          {addError && <div className="form-error" role="alert">{addError}</div>}
          <Input
            label="Group *"
            list="admin-service-group-options"
            placeholder="e.g. GST, Income Tax, Payroll"
            value={addForm.groupName}
            onChange={(e) => setAddForm({ ...addForm, groupName: e.target.value })}
            disabled={addSaving}
          />
          <datalist id="admin-service-group-options">
            {Array.from(new Set(services.map((s) => s.groupName))).map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
          <Input
            label="Service Name *"
            placeholder="e.g. GST REFUND ON EXPORT OF GOODS"
            value={addForm.name}
            onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
            disabled={addSaving}
          />
          <p className="admin-rule-hint">You can set its default due date after adding.</p>
        </form>
      </Modal>
    </AdminLayout>
  );
}

export default AdminServices;
